import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import useConfiguratorStore from '../store/configuratorStore';
import InlinePoleCalculator from './InlinePoleCalculator';

const Container = styled.div`
  margin-bottom: 1.5rem;
`;

const AttributesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const AttributeGroup = styled(motion.div)`
  margin-bottom: 0.75rem;
  border: 1px solid var(--gray-200);
  border-radius: var(--rounded-lg);
  padding: 1.25rem;
  background-color: white;
  transition: var(--transition-fast);
  
  ${props => {
    const orangeBorderColor = 'var(--configurator-step-warning-border, #FF8F00)'; 
    const specialAttributes = ['poleType', 'material', 'size'];
    const isSpecialAttribute = props.attributeName && specialAttributes.includes(props.attributeName);

    if (props.active) {
      return `
        border-color: var(--primary-color);
        box-shadow: var(--shadow-md);
      `;
    } 
    
    if (props.completed && !props.active) {
      if (isSpecialAttribute) {
        return `
          border-color: ${orangeBorderColor}; 
        `;
      }
      return `
        border-color: var(--success-color);
      `;
    }
    return ''; 
  }}
  
  &:hover {
    box-shadow: var(--shadow-sm);
    border-color: ${props => {
      const orangeBorderColor = 'var(--configurator-step-warning-border, #FF8F00)';
      const specialAttributes = ['poleType', 'material', 'size'];
      const isSpecialAttribute = props.attributeName && specialAttributes.includes(props.attributeName);

      if (props.active) {
        return 'var(--primary-color)';
      }
      if (props.completed && !props.active) {
        if (isSpecialAttribute) {
          return orangeBorderColor;
        }
        return 'var(--success-color)';
      }
      return 'var(--gray-300)'; 
    }};
  }
`;

const AttributeLabel = styled.label`
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--gray-700);
  margin-bottom: 0.5rem;
  display: block;
`;

const AttributeDescription = styled.p`
  font-size: var(--text-xs);
  color: var(--gray-500);
  margin-bottom: 1rem;
`;

const SelectWrapper = styled.div`
  position: relative;
  flex-grow: 1; 
  min-width: 200px; /* Default min-width for select wrappers */

  &:after {
    content: '▼';
    font-size: 0.7rem;
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--gray-500);
    pointer-events: none;
    z-index: 1;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  padding-right: ${props => props.hasCalculatedBadge ? '8rem' : '2.5rem'};
  border-radius: var(--rounded-md);
  border: 1px solid var(--gray-300);
  background-color: white;
  font-size: var(--text-md);
  color: var(--gray-800);
  appearance: none;
  transition: var(--transition-fast);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(0, 112, 243, 0.1);
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const RadioLabel = styled.label`
  display: inline-flex;
  flex-direction: ${props => props.hasDescription ? 'column' : 'row'};
  align-items: ${props => props.hasDescription ? 'flex-start' : 'center'};
  gap: ${props => props.hasDescription ? '0.5rem' : '0.75rem'};
  padding: ${props => props.hasDescription ? '1rem 1.25rem' : '0.75rem 1.25rem'};
  border-radius: var(--rounded-md);
  border: 2px solid ${props => props.checked ? 'var(--primary-color)' : 'var(--gray-300)'};
  background-color: ${props => props.checked ? 'var(--primary-light)' : 'white'};
  font-size: var(--text-sm);
  color: ${props => props.checked ? 'var(--primary-color)' : 'var(--gray-700)'};
  cursor: pointer;
  transition: var(--transition-fast);
  position: relative;
  flex: ${props => props.hasDescription ? '1' : 'auto'};
  min-width: ${props => props.hasDescription ? '200px' : 'auto'};

  &:hover {
    border-color: ${props => props.checked ? 'var(--primary-color)' : 'var(--gray-400)'};
    transform: translateY(-2px);
    box-shadow: var(--shadow-sm);
  }
`;

const RadioInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const CheckmarkIcon = styled.span`
  font-size: 1em;
  color: var(--primary-color);
`;

const PoleOptionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
`;

const PoleOptionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
`;

const PoleOptionTitle = styled.span`
  font-weight: 600;
  font-size: var(--text-sm);
  line-height: 1.2;
`;

const PoleOptionDescription = styled.span`
  font-size: 0.75rem;
  color: ${props => props.checked ? 'rgba(255, 143, 0, 0.75)' : 'var(--gray-500)'};
  font-weight: 400;
  line-height: 1.3;
  letter-spacing: 0.01em;
`;

const NoAttributesMessage = styled.p`
  padding: 2rem;
  color: var(--gray-500);
  font-style: italic;
  text-align: center;
  background-color: var(--gray-50);
  border-radius: var(--rounded-lg);
`;

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const StepNumber = styled.div`
  width: 24px;
  height: 24px;
  border-radius: var(--rounded-full);
  background-color: var(--primary-color);
  color: white;
  font-size: var(--text-xs);
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
`;

const AttributeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const AttributeInfo = styled.div`
  flex: 1;
`;

const AttributeStatus = styled.div`
  font-size: var(--text-xs);
  padding: 0.25rem 0.5rem;
  border-radius: var(--rounded-sm);
  font-weight: 500;
  background-color: ${props => {
    const subtleLightGreenBg = 'rgba(34, 197, 94, 0.08)';
    const defaultBgColor = 'var(--gray-100, #F3F4F6)';    

    if (props.selected) {
      return subtleLightGreenBg;
    }
    return defaultBgColor;
  }};
  color: ${props => {
    const subtleLightGreenText = 'rgba(34, 197, 94, 0.8)';
    const defaultTextColor = 'var(--gray-500, #6B7280)';     

    if (props.selected) {
      return subtleLightGreenText;
    }
    return defaultTextColor;
  }};
`;

const CascadeNavigation = styled.div`
  display: flex;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid var(--gray-200);
  padding-bottom: 1rem;
`;

const CascadeStep = styled.div`
  position: relative;
  flex: 1;
  padding: 0 0.5rem;
  
  &:not(:last-child):after {
    content: '';
    position: absolute;
    right: -8px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23d1d5db'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M9 5l7 7-7 7'%3E%3C/path%3E%3C/svg%3E");
    background-size: contain;
    background-repeat: no-repeat;
  }
`;

const StepLabel = styled.div`
  font-size: var(--text-xs);
  color: var(--gray-500);
  margin-bottom: 0.25rem;
`;

const StepValue = styled.div`
  font-size: var(--text-sm);
  font-weight: ${props => props.active ? '600' : '400'};
  color: ${props => props.active ? 'var(--primary-color)' : 'var(--gray-700)'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SizeAttributeInputRow = styled.div`
  display: flex;
  align-items: center; 
  gap: 1rem; 
  margin-top: 0.5rem;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const InlineHelperContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center; 
  justify-content: flex-end;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const InlineHelperText = styled.p`
  font-size: var(--text-sm);
  color: var(--gray-600); 
  margin: 0; 
  line-height: 1.4;
  white-space: nowrap;
  font-weight: 500;
  
  @media (max-width: 768px) {
    margin-bottom: 0.25rem;
  }
`;

const CalcHelperLinkButton = styled.button`
  background: var(--primary-color);
  border: 1px solid var(--primary-color);
  color: white;
  font-size: var(--text-sm); 
  font-weight: 600; 
  cursor: pointer;
  padding: 0.6rem 1rem; 
  border-radius: var(--rounded-lg);
  display: inline-flex;
  align-items: center;
  gap: 0.5rem; 
  transition: all 0.2s ease;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(255, 143, 0, 0.15);

  svg {
    width: 1em; 
    height: 1em;
    stroke-width: 2;
  }

  &:hover {
    background: var(--primary-dark-color, #e67e00);
    border-color: var(--primary-dark-color, #e67e00);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 143, 0, 0.25);
  }
  
  &:active {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(255, 143, 0, 0.2);
  }
`;

const CalculatedSizeBadge = styled.span`
  position: absolute;
  right: 2.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: var(--success-light);
  color: var(--success-color);
  font-size: var(--text-xs);
  font-weight: 600;
  padding: 0.2rem 0.4rem;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  border: 1px solid var(--success-color);
  z-index: 2;
  pointer-events: none;
  white-space: nowrap;
  
  &::before {
    content: '✓';
    font-size: 0.8em;
  }
`;

// Add Shipping-style components
const SizeSelectionCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #f1f5f9;
  margin-top: 0.5rem;
  overflow: hidden;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const SizeSelectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 60px;
  
  &:hover {
    background: rgba(0, 0, 0, 0.01);
  }
  
  @media (max-width: 640px) {
    padding: 0.875rem 1rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem 0.875rem;
    min-height: auto;
  }
`;

const SizeHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0; /* Allow text to truncate if needed */
  
  @media (max-width: 640px) {
    gap: 0.5rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.375rem;
  }
`;

const SizeIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-color) 0%, #e67e00 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
  
  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }
`;

const SizeHeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0; /* Allow text to truncate */
  overflow: hidden;
`;

const SizeTitle = styled.h4`
  font-size: var(--text-md);
  font-weight: 600;
  color: var(--gray-900);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 480px) {
    font-size: var(--text-sm);
  }
`;

const SizeSubtitle = styled.p`
  font-size: var(--text-sm);
  color: var(--gray-600);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 640px) {
    display: none; /* Hide subtitle on smaller screens to save space */
  }
`;

const SizeHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  
  @media (max-width: 480px) {
    gap: 0.25rem;
  }
`;

const ExpandText = styled.span`
  font-size: var(--text-sm);
  color: var(--gray-500);
  font-weight: 500;
  
  @media (max-width: 480px) {
    display: none; /* Hide "Expand" text on very small screens */
  }
`;

const ExpandChevron = styled.div`
  color: var(--gray-400);
  transition: transform 0.2s ease;
  transform: ${props => props.expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const SizeSelectionContent = styled(motion.div)`
  background: #f8fafc;
  border-top: 1px solid #f1f5f9;
  padding: 1.25rem;
  overflow: hidden;
`;

const SizeDropdownContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const CompactSelect = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--gray-300);
  border-radius: 6px;
  font-size: var(--text-sm);
  background: white;
  min-width: 180px;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(255, 143, 0, 0.1);
  }
  
  &:hover {
    border-color: var(--gray-400);
  }
`;

const CalcHelperButton = styled.button`
  padding: 0.4rem 0.75rem;
  background-color: #FF8F00;
  color: white;
  border-radius: var(--rounded-md);
  font-size: var(--text-xs);
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e67e00;
    transform: translateY(-1px);
    box-shadow: 0 2px 5px rgba(255, 143, 0, 0.3);
  }
`;

// Standalone Pole Calculator Components
const PoleCalculatorSection = styled(motion.div)`
  margin: 1.5rem 0;
  padding: 1rem;
  background: white;
  border: 1px solid var(--primary-color);
  border-radius: var(--rounded-lg);
  box-shadow: var(--shadow-sm);
  position: relative;
  overflow: hidden;
  transition: var(--transition-fast);

  &:hover {
    box-shadow: var(--shadow-md);
    border-color: var(--primary-color);
  }
`;

const CalculatorHeaderSection = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1rem;
  gap: 1rem;
`;

const CalculatorHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
`;

const CalculatorIconLarge = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--primary-color) 0%, #e67e00 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(255, 143, 0, 0.2);
`;

const CalculatorHeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const CalculatorTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--gray-900);
  margin: 0;
  line-height: 1.3;
`;

const CalculatorSubtitle = styled.p`
  font-size: 0.8125rem;
  color: var(--gray-500);
  margin: 0;
  line-height: 1.4;
`;

const CalculatorBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  background: rgba(255, 143, 0, 0.1);
  color: var(--primary-color);
  font-size: 0.625rem;
  font-weight: 600;
  border-radius: 5px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  box-shadow: none;
  white-space: nowrap;
  border: 1px solid rgba(255, 143, 0, 0.2);

  &::before {
    content: '✨';
    font-size: 0.75rem;
  }
`;

const CalculatorContentArea = styled.div`
  background: transparent;
  border-radius: 0;
  padding: 0;
  box-shadow: none;
`;

const RecalculateButton = styled.button`
  padding: 0.75rem 0.875rem;
  background: white;
  color: var(--gray-700);
  border: 1px solid var(--gray-300);
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;

  &:hover {
    background: var(--gray-50);
    border-color: var(--gray-400);
  }
`;

const SizeSelectionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const CalculatedIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 0.75rem;
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(34, 197, 94, 0.08) 100%);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 6px;
  font-size: 0.8125rem;
  color: var(--success-color);
  font-weight: 500;
  flex: 1;
  min-width: 200px;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const attributeVariants = {
  hidden: { opacity: 0, y: 20, height: 0, marginBottom: 0 },
  visible: { 
    opacity: 1, 
    y: 0,
    height: 'auto',
    marginBottom: '1rem',
    transition: { 
      type: 'spring',
      duration: 0.5
    }
  },
  exit: { 
    opacity: 0,
    y: -10,
    height: 0,
    marginBottom: 0,
    transition: { 
      duration: 0.3 
    }
  }
};

const attributeLabels = {
  poleType: { label: 'Pole Type', description: 'Select the type of pole for your application' },
  material: { label: 'Material', description: 'Choose the material best suited for your environment' },
  thickness: { label: 'Thickness', description: 'Select the thickness of the pole' },
  size: { label: 'Size', description: 'Choose the appropriate size' },
  lengthRange: { label: 'Length Range', description: 'Select the length range for your telescopic pole' },
  type: { label: 'Type', description: 'Select the product type' },
  grade: { label: 'Grade', description: 'Choose between standard or food grade' },
  sides: { label: 'Sides', description: 'Select the side configuration' },
  slide: { label: 'Slide', description: 'Choose whether to include a slide' },
  addonType: { label: 'Add-on Type', description: 'Select the type of add-on' },
  heightRange: { label: 'Height Range', description: 'Select the appropriate height range' },
  width: { label: 'Width', description: 'Choose the width dimension' },
  length: { label: 'Length', description: 'Choose the length dimension' },
  height: { label: 'Height', description: 'Choose the height dimension' },
  loopLength: { label: 'Loop Length', description: 'Select the appropriate loop length' }
};

const getAttributeInputType = (attributeName) => {
  if (['material', 'grade', 'sides', 'slide', 'poleType'].includes(attributeName)) return 'radio';
  return 'select';
};

const getCategoryAttributeOrder = (categoryId) => {
  const attributeOrders = {
    poles: ['poleType', 'material', 'thickness', 'size', 'lengthRange'],
    spare_parts: ['type'],
    decks: ['type', 'material', 'grade', 'sides', 'slide'],
    unloading_stations: ['type', 'size', 'material', 'heightRange', 'addonType'],
    big_bags: ['width', 'length', 'height', 'loopLength']
  };
  return attributeOrders[categoryId] || [];
};

function AttributeConfigurator({ isDirectConfiguration = false, onConfigurationComplete }) {
  const { 
    selectedMainCategory, selectedProduct, selectedSubcategory, organizedProducts,
    selectedAttributes, setSelectedAttribute, attributeProductMap,
    setSelectedProduct, calculateQuote, setCalculatedLengthInfo
  } = useConfiguratorStore();
  
  // State for inline pole calculator
  const [calculatedSizeInfo, setCalculatedSizeInfo] = useState(null);
  const [isCalculatorCollapsed, setIsCalculatorCollapsed] = useState(false);

  // Handle pole calculator result
  const handleCalculationComplete = (result) => {
    setCalculatedSizeInfo(result);
    setCalculatedLengthInfo(result); // Save to store for cart items
    setSelectedAttribute('size', result.calculatedSize);
    setIsCalculatorCollapsed(true); // Collapse calculator after applying result
    calculateQuote();
  };

  // Handle recalculate - expand calculator again
  const handleRecalculate = () => {
    setIsCalculatorCollapsed(false);
  };

  // Get selected pole type for calculator pre-filling
  const getSelectedPoleType = () => {
    return selectedAttributes?.poleType || null;
  };

  // Determine if standalone calculator should be shown
  const shouldShowStandaloneCalculator = () => {
    return isPoleDirectConfig &&
           selectedAttributes?.poleType &&
           selectedAttributes?.poleType.trim() !== '' &&
           selectedAttributes?.material &&
           selectedAttributes?.material.trim() !== '';
  };
  
  React.useEffect(() => {
    if (isDirectConfiguration && selectedMainCategory === 'poles' && 
        (!selectedAttributes || Object.keys(selectedAttributes).length === 0)) {
      setSelectedAttribute('poleType', '');
      setSelectedAttribute('material', '');
      setSelectedAttribute('thickness', '');
      setSelectedAttribute('size', '');
    }
  }, [isDirectConfiguration, selectedMainCategory, selectedAttributes, setSelectedAttribute]);
  
  const didCallComplete = React.useRef(false);
  
  React.useEffect(() => {
    if (isDirectConfiguration && selectedMainCategory === 'poles' && selectedProduct) {
      if (onConfigurationComplete && !didCallComplete.current) {
        didCallComplete.current = true;
        const productObj = organizedProducts[selectedMainCategory]?.find(p => p.id === selectedProduct);
        if (productObj) setTimeout(() => onConfigurationComplete(productObj), 0);
      }
    } else if (!selectedProduct) didCallComplete.current = false;
  }, [isDirectConfiguration, selectedMainCategory, selectedProduct, organizedProducts, onConfigurationComplete]);
  
  React.useEffect(() => {
    if (isDirectConfiguration && selectedMainCategory === 'poles') {
      const attributeValues = { poleType: new Set(), material: new Set(), thickness: new Set(), size: new Set() };
      organizedProducts[selectedMainCategory]?.forEach(product => {
        if (product.attributes) Object.entries(product.attributes).forEach(([key, value]) => {
          if (attributeValues[key]) attributeValues[key].add(value);
        });
      });
    }
  }, [isDirectConfiguration, selectedMainCategory, organizedProducts]);
  
  // Separate useEffect for auto-selection logic to prevent race conditions
  React.useEffect(() => {
    if (isDirectConfiguration && selectedMainCategory === 'poles') {
      const displayedAttrs = ['poleType', 'material', 'size'];
      const hasAllDisplayed = displayedAttrs.every(attr => selectedAttributes[attr] && selectedAttributes[attr].trim() !== '');
      
      if (hasAllDisplayed) {
        let updatedAttrs = {...selectedAttributes};
        
        // Thickness is now handled in handleAttributeChange, no need for auto-setting here
        // Just ensure we have the current thickness for product matching
        if (selectedAttributes.size && !updatedAttrs.thickness) {
          updatedAttrs.thickness = ['2000-2100mm', '2101-2250mm', '2251-2450mm'].includes(selectedAttributes.size) ? '60*2,5mm' : '60*1,5mm';
        }
        
        // Find matching products
        const matchingProducts = getMatchingProducts(updatedAttrs);
        if (matchingProducts.length >= 1 && selectedProduct !== matchingProducts[0].id) {
          setSelectedProduct(matchingProducts[0].id);
          calculateQuote();
          if (onConfigurationComplete && !didCallComplete.current) {
            didCallComplete.current = true;
            onConfigurationComplete(matchingProducts[0]);
          }
        }
      }
    }
  }, [selectedAttributes?.poleType, selectedAttributes?.material, selectedAttributes?.size, isDirectConfiguration, selectedMainCategory, selectedProduct]);
  
  const productObject = selectedMainCategory && selectedProduct && organizedProducts[selectedMainCategory] 
    ? organizedProducts[selectedMainCategory].find(p => p.id === selectedProduct) : null;
    
  if (!selectedMainCategory) return <NoAttributesMessage>Please select a category first</NoAttributesMessage>;
  
  const getAttributeOptions = (attributeName) => {
    if (attributeName === 'poleType' && isDirectConfiguration && selectedMainCategory === 'poles') {
      return ['Springhead Pole', 'Flow Pole'];
    }
    if (selectedMainCategory && attributeProductMap[selectedMainCategory]?.[attributeName]) {
      return Object.keys(attributeProductMap[selectedMainCategory][attributeName]).sort((a, b) => a.localeCompare(b));
    }
    const options = new Set();
    const relatedProducts = organizedProducts[selectedMainCategory].filter(p => selectedSubcategory ? p.subcategoryId === selectedSubcategory : true);
    relatedProducts.forEach(product => {
      if (product.attributes && product.attributes[attributeName]) options.add(product.attributes[attributeName]);
    });
    return Array.from(options);
  };
  
  const getViableOptions = (attributeName, allOptions) => {
    if (attributeName === 'poleType' && isDirectConfiguration && selectedMainCategory === 'poles') {
      return ['Springhead Pole', 'Flow Pole'];
    }
    
    // For size selection in pole direct configuration, ignore auto-set thickness to prevent circular dependency
    if (attributeName === 'size' && isDirectConfiguration && selectedMainCategory === 'poles') {
      const currentAttributes = {...selectedAttributes};
      delete currentAttributes[attributeName];
      // Remove thickness from consideration to allow full size re-selection
      delete currentAttributes.thickness;
      
      // Get all available sizes from the data instead of hardcoded ranges
      const allAvailableSizes = attributeProductMap[selectedMainCategory]?.['size'] ? 
        Object.keys(attributeProductMap[selectedMainCategory]['size']) : [];
      
      // Apply filtering based on pole type and material only
      let matchingSets = Object.entries(currentAttributes).filter(([_, v]) => v && v !== '').map(([attr, value]) => {
        return attributeProductMap[selectedMainCategory]?.[attr]?.[value] ? new Set(attributeProductMap[selectedMainCategory][attr][value]) : new Set();
      });
      
      if (matchingSets.length === 0) {
        return allAvailableSizes; // If no constraints, return all available sizes
      }
      
      // Filter size options based on available products (excluding thickness constraint)
      return allAvailableSizes.filter(sizeOption => {
        const testAttributes = {...currentAttributes, size: sizeOption};
        let testSets = Object.entries(testAttributes).filter(([_, v]) => v && v !== '').map(([attr, value]) => {
          return attributeProductMap[selectedMainCategory]?.[attr]?.[value] ? new Set(attributeProductMap[selectedMainCategory][attr][value]) : new Set();
        });
        if (testSets.length === 0) return true;
        const intersection = testSets.reduce((acc, set) => new Set([...acc].filter(id => set.has(id))), testSets[0] || new Set());
        return intersection.size > 0;
      });
    }
    
    const currentAttributes = {...selectedAttributes}; delete currentAttributes[attributeName];
    const poleRanges = ['1000-1300mm', '1301-1450mm', '1451-1650mm', '1651-1850mm', '1851-1950mm', '2000-2100mm', '2101-2250mm', '2251-2450mm'];
    if (Object.keys(currentAttributes).length === 0) {
      if (attributeName === 'thickness') {
        const isPoleRollingSpringhead = currentAttributes.poleType === 'Rolling Springhead Pole';
        if (isPoleRollingSpringhead) {
          if (currentAttributes.material === 'Stainless Steel') return allOptions;
          if (currentAttributes.size) return ['2000-2100mm', '2101-2250mm', '2251-2450mm'].includes(currentAttributes.size) ? allOptions : allOptions.filter(o => o === '60*1,5mm');
          return allOptions;
        }
        return allOptions.filter(o => o === '60*2,5mm' ? (currentAttributes.size ? ['2000-2100mm', '2101-2250mm', '2251-2450mm'].includes(currentAttributes.size) : true) : true);
      }
      if (attributeName === 'material' && currentAttributes.poleType === 'Rolling Springhead Pole') return ['Galvanized Steel', 'Stainless Steel'];
      return allOptions;
    }
    return allOptions.filter(optionValue => {
      const testAttributes = {...currentAttributes, [attributeName]: optionValue};
      if (attributeName === 'thickness' && optionValue === '60*2,5mm' && testAttributes.size && !['2000-2100mm', '2101-2250mm', '2251-2450mm'].includes(testAttributes.size)) return false;
      let matchingSets = Object.entries(testAttributes).filter(([_, v]) => v && v !== '').map(([attr, value]) => {
        return attributeProductMap[selectedMainCategory]?.[attr]?.[value] ? new Set(attributeProductMap[selectedMainCategory][attr][value]) : new Set();
      });
      if (matchingSets.length === 0) return true;
      const intersection = matchingSets.reduce((acc, set) => new Set([...acc].filter(id => set.has(id))), matchingSets[0] || new Set());
      return intersection.size > 0;
    });
  };
  
  const handleAttributeChange = (attributeName, value) => {
    // Clear calculated length info when attributes that affect pole calculation change
    const poleCalculationAttributes = ['poleType', 'material', 'thickness'];
    if (selectedMainCategory === 'poles' && poleCalculationAttributes.includes(attributeName)) {
      setCalculatedSizeInfo(null);
      setCalculatedLengthInfo(null);
      setIsCalculatorCollapsed(false); // Expand calculator when changing pole type or material
    }
    
    if (attributeName === 'poleType' && isDirectConfiguration && selectedMainCategory === 'poles') {
      // Only clear downstream attributes instead of all attributes
      const orderedAttrs = getCategoryAttributeOrder(selectedMainCategory);
      const currentIndex = orderedAttrs.indexOf(attributeName);
      
      // Clear only attributes that come after the current one
      orderedAttrs.forEach((attr, index) => {
        if (index > currentIndex) {
          setSelectedAttribute(attr, '');
        }
      });
      
      setSelectedProduct(null); 
      didCallComplete.current = false;
      setSelectedAttribute(attributeName, value); 
      return;
    }
    const orderedAttrs = getCategoryAttributeOrder(selectedMainCategory); 
    const currentIndex = orderedAttrs.indexOf(attributeName);
    
    // Handle size selection specially to avoid the cascade/auto-thickness conflict
    if (isDirectConfiguration && selectedMainCategory === 'poles' && attributeName === 'size') {
      // For size selection, preserve poleType and material, set size, then auto-set thickness
      const newAttributes = {
        poleType: selectedAttributes.poleType || '',
        size: value,
        thickness: ['2000-2100mm', '2101-2250mm', '2251-2450mm'].includes(value) ? '60*2,5mm' : '60*1,5mm'
      };
      
      // Only preserve material if it actually has a value
      if (selectedAttributes.material && selectedAttributes.material.trim() !== '') {
        newAttributes.material = selectedAttributes.material;
      }
      
      // Set all attributes at once to avoid cascading conflicts
      Object.entries(newAttributes).forEach(([attr, val]) => setSelectedAttribute(attr, val));
      return;
    }
    
    // Standard cascade logic for other attributes
    const newAttributes = {};
    Object.entries(selectedAttributes).forEach(([key, val]) => {
      const attrIndex = orderedAttrs.indexOf(key);
      if (attrIndex !== -1 && attrIndex < currentIndex) newAttributes[key] = val;
    });
    newAttributes[attributeName] = value;
    Object.entries(newAttributes).forEach(([attr, val]) => setSelectedAttribute(attr, val));
  };
  
  const getMatchingProducts = (attributes) => {
    if (!selectedMainCategory || !organizedProducts[selectedMainCategory]) return [];
    const isFlowPole = attributes.poleType === 'Flow Pole';
    let matching = organizedProducts[selectedMainCategory].filter(product => 
      Object.entries(attributes).every(([key, value]) => {
        if (!value) return true;
        if (isFlowPole && key === 'poleType' && product.attributes?.[key]?.includes('Flow')) return true;
        return product.attributes?.[key] === value;
      })
    );
    if (matching.length === 0 && isFlowPole) {
      matching = organizedProducts[selectedMainCategory].filter(p => 
        p.attributes?.poleType?.includes('Flow') &&
        (!attributes.material || p.attributes.material === attributes.material) &&
        (!attributes.size || p.attributes.size === attributes.size)
      );
    }
    return matching;
  };
  
  const getConfigurableAttributes = () => {
    const ordered = getCategoryAttributeOrder(selectedMainCategory);
    if (isDirectConfiguration && selectedMainCategory === 'poles') return ['poleType', 'material', 'size'];
    if (productObject) {
      const productAttrs = Object.keys(productObject.attributes);
      return ordered.filter(attr => productAttrs.includes(attr));
    }
    const allAttrs = new Set();
    const relevantProducts = organizedProducts[selectedMainCategory]?.filter(p => selectedSubcategory ? p.subcategoryId === selectedSubcategory : true) || [];
    relevantProducts.forEach(p => { if (p.attributes) Object.keys(p.attributes).forEach(attr => allAttrs.add(attr)); });
    return ordered.filter(attr => allAttrs.has(attr));
  };
  
  const shouldShowAttribute = (attributeName) => {
    const ordered = getCategoryAttributeOrder(selectedMainCategory); 
    const attrIndex = ordered.indexOf(attributeName);
    
    // First attribute is always visible
    if (attrIndex === 0) return true;
    
    // For poles in direct configuration, show all steps immediately
    if (isDirectConfiguration && selectedMainCategory === 'poles') {
      // Show poleType, material, and size steps from the start
      if (['poleType', 'material', 'size'].includes(attributeName)) {
        return true;
      }
      // Show thickness if all previous are selected (though this might be auto-selected)
      if (attributeName === 'thickness') {
        return selectedAttributes['poleType'] && selectedAttributes['poleType'].trim() !== '' &&
               selectedAttributes['material'] && selectedAttributes['material'].trim() !== '' &&
               selectedAttributes['size'] && selectedAttributes['size'].trim() !== '';
      }
    }
    
    // For other categories, check all previous attributes are selected
    for (let i = 0; i < attrIndex; i++) {
      if (!selectedAttributes[ordered[i]] || selectedAttributes[ordered[i]].trim() === '') {
        return false;
      }
    }
    return true;
  };
  
  const isPoleDirectConfig = isDirectConfiguration && selectedMainCategory === 'poles';
  const allConfigurableAttributes = getConfigurableAttributes();
  const attributesToDisplay = allConfigurableAttributes.filter(shouldShowAttribute);
  
  if (attributesToDisplay.length === 0 && !(selectedMainCategory === 'poles' && isPoleDirectConfig)) {
    return <NoAttributesMessage>No configurable attributes for this category</NoAttributesMessage>;
  }
  
  const orderedNavAttributes = getCategoryAttributeOrder(selectedMainCategory).filter(attr => allConfigurableAttributes.includes(attr));
  
  return (
    <Container>
      {!isPoleDirectConfig && (
        <CascadeNavigation>
          {orderedNavAttributes.map((attrName, index) => {
            const isActive = shouldShowAttribute(attrName); const hasVal = Boolean(selectedAttributes[attrName]);
            const labelInfo = attributeLabels[attrName] || { label: attrName };
            return (
              <CascadeStep key={attrName}>
                <StepLabel>{index + 1}. {labelInfo.label}</StepLabel>
                <StepValue active={isActive || hasVal} title={selectedAttributes[attrName] || ''}>
                  {selectedAttributes[attrName] || (isActive ? 'Select...' : '-')}
                </StepValue>
              </CascadeStep>
            );
          })}
        </CascadeNavigation>
      )}
      
      <AttributesGrid>
        {attributesToDisplay.map((attributeName, stepIndex) => {
          const allOptions = getAttributeOptions(attributeName);
          const viableOptions = getViableOptions(attributeName, allOptions);
          const inputType = getAttributeInputType(attributeName);
          const labelInfo = attributeLabels[attributeName] || { label: attributeName, description: `Select ${attributeName}` };

          // For poles in direct configuration, show all steps even with 1 option to maintain progression
          if (viableOptions.length <= 1 && !(selectedMainCategory === 'poles' && (attributeName === 'size' || (isDirectConfiguration && ['poleType', 'material'].includes(attributeName))))) return null;

          const firstIncompleteAttr = isPoleDirectConfig && orderedNavAttributes.find(attr => !selectedAttributes[attr] || selectedAttributes[attr] === '');
          const isCurrentStep = isPoleDirectConfig && attributeName === firstIncompleteAttr;
          const isCompletedStep = isPoleDirectConfig && selectedAttributes[attributeName] && selectedAttributes[attributeName] !== '';

          return (
            <React.Fragment key={`${attributeName}-fragment`}>
            <AttributeGroup 
              key={attributeName} 
              variants={attributeVariants} 
              initial="hidden" 
              animate="visible" 
              exit="exit" 
              active={isCurrentStep} 
              completed={isCompletedStep}
              attributeName={attributeName} 
            >
              <AttributeHeader>
                <AttributeInfo>
                  <StepIndicator>
                    <StepNumber>{stepIndex + 1}</StepNumber>
                    <AttributeLabel>{labelInfo.label}</AttributeLabel>
                  </StepIndicator>
                  <AttributeDescription>{labelInfo.description}</AttributeDescription>
                </AttributeInfo>
                {selectedAttributes[attributeName] && (
                  <AttributeStatus selected attributeName={attributeName}> 
                    Selected: {selectedAttributes[attributeName]}
                  </AttributeStatus>
                )}
              </AttributeHeader>
              
              {selectedMainCategory === 'poles' && attributeName === 'size' ? (
                <>
                  <SizeSelectionRow>
                    <SelectWrapper style={{ flex: 1, minWidth: '200px' }}>
                      <Select
                        value={selectedAttributes[attributeName] || ''}
                        onChange={(e) => handleAttributeChange(attributeName, e.target.value)}
                      >
                        <option value="">Select {labelInfo.label}</option>
                        {viableOptions.map((option, idx) => (
                          <option key={idx} value={option}>
                            {option}
                          </option>
                        ))}
                      </Select>
                    </SelectWrapper>
                    {calculatedSizeInfo && isCalculatorCollapsed && (
                      <CalculatedIndicator>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Calculated: {calculatedSizeInfo.calculatedLength}</span>
                      </CalculatedIndicator>
                    )}
                    {isCalculatorCollapsed && (
                      <RecalculateButton onClick={handleRecalculate}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '14px', height: '14px' }}>
                          <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Recalculate
                      </RecalculateButton>
                    )}
                  </SizeSelectionRow>
                </>
              ) : inputType === 'select' ? (
                <SelectWrapper style={{ minWidth: '100%' }}>
                  <Select value={selectedAttributes[attributeName] || ''} onChange={(e) => handleAttributeChange(attributeName, e.target.value)}>
                    <option value="">Select {labelInfo.label}</option>
                    {viableOptions.map((option, idx) => <option key={idx} value={option}>{option}</option>)}
                  </Select>
                </SelectWrapper>
              ) : (
                <RadioGroup>
                  {viableOptions.map((option, idx) => {
                    const isChecked = selectedAttributes[attributeName] === option;
                    const getPoleImage = () => {
                      if (attributeName === 'poleType') {
                        if (option === 'Lockhead Pole') return "https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/poles/Poles%20Section.avif";
                        if (option === 'Springhead Pole') return "https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/poles.images/Springhead-section.avif";
                        if (option === 'Rolling Springhead Pole') return "https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/poles.images/Rolling%20Springhead%20Section.avif";
                        if (option === 'Flow Pole') return "https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/poles.images/Flow-Section.avif";
                      } return null;
                    };
                    const getPoleDescription = () => {
                      if (attributeName === 'poleType') {
                        if (option === 'Springhead Pole') return "Best when receiving full big bags";
                        if (option === 'Flow Pole') return "Best when filling and emptying big bags internally";
                      }
                      return null;
                    };
                    const poleImage = getPoleImage();
                    const poleDescription = getPoleDescription();
                    const hasDescription = attributeName === 'poleType' && poleDescription;

                    return (
                      <RadioLabel key={idx} checked={isChecked} hasDescription={hasDescription}>
                        <RadioInput type="radio" name={attributeName} value={option} checked={isChecked} onChange={() => handleAttributeChange(attributeName, option)} />
                        {hasDescription ? (
                          <PoleOptionHeader>
                            {isChecked && <CheckmarkIcon>✓</CheckmarkIcon>}
                            {poleImage && <img src={poleImage} alt={option} style={{ height: '40px', width: 'auto', objectFit: 'contain', transition: 'transform 0.2s ease', transform: isChecked ? 'scale(1.05)' : 'scale(1)'}} />}
                            <PoleOptionContent>
                              <PoleOptionTitle>{option}</PoleOptionTitle>
                              <PoleOptionDescription checked={isChecked}>{poleDescription}</PoleOptionDescription>
                            </PoleOptionContent>
                          </PoleOptionHeader>
                        ) : (
                          <>
                            {isChecked && <CheckmarkIcon>✓</CheckmarkIcon>}
                            {poleImage && <img src={poleImage} alt={option} style={{ height: '32px', width: 'auto', objectFit: 'contain', marginRight: '0.25rem', transition: 'transform 0.2s ease', transform: isChecked ? 'scale(1.1)' : 'scale(1)'}} />}
                            {option}
                          </>
                        )}
                      </RadioLabel>
                    );
                  })}
                </RadioGroup>
              )}
            </AttributeGroup>

            {/* Insert standalone calculator after material step */}
            {attributeName === 'material' && shouldShowStandaloneCalculator() && !isCalculatorCollapsed && (
              <PoleCalculatorSection
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              >
                <CalculatorHeaderSection>
                  <CalculatorHeaderLeft>
                    <CalculatorIconLarge>🧮</CalculatorIconLarge>
                    <CalculatorHeaderContent>
                      <CalculatorTitle>Calculate Your Perfect Pole Length</CalculatorTitle>
                      <CalculatorSubtitle>Get the ideal size based on your big bag dimensions</CalculatorSubtitle>
                    </CalculatorHeaderContent>
                  </CalculatorHeaderLeft>
                  <CalculatorBadge>Recommended</CalculatorBadge>
                </CalculatorHeaderSection>

                <CalculatorContentArea>
                  <InlinePoleCalculator
                    preselectedPoleType={getSelectedPoleType()}
                    onCalculationComplete={handleCalculationComplete}
                    onClose={null}
                    embedded={true}
                  />
                </CalculatorContentArea>
              </PoleCalculatorSection>
            )}
            </React.Fragment>
          );
        })}
      </AttributesGrid>
      
    </Container>
  );
}

export default AttributeConfigurator;
